import { init as initDash0 } from '@dash0/sdk-web';
import { consumeFederatedSessionFragment, consumeImpersonationFragment } from 'korint-sdk';

/**
 * Must run before `initDash0`: the browser's `PerformanceNavigationTiming` entry
 * for a `/impersonate#impersonationToken=…` load captures the fragment as an
 * immutable snapshot, which Dash0's navigation instrumentation then reports
 * verbatim. Redirecting first means Dash0 never sees this load.
 */
const isImpersonationHandoff = consumeImpersonationFragment() || consumeFederatedSessionFragment();

const dash0EndpointUrl = import.meta.env.VITE_DASH0_ENDPOINT_URL;
const dash0WebsiteToken = import.meta.env.VITE_DASH0_WEBSITE_TOKEN;

if (
  !isImpersonationHandoff &&
  import.meta.env.VITE_DASH0_ENABLED === 'true' &&
  dash0EndpointUrl &&
  dash0WebsiteToken
) {
  initDash0({
    serviceName: import.meta.env.VITE_TENANT_ID?.toLowerCase() || 'uniq',
    endpoint: { url: dash0EndpointUrl, authToken: dash0WebsiteToken },
    propagators: [
      {
        type: 'traceparent',
        match: [
          /^https?:\/\/localhost(:\d+)?(\/|$)/,
          /^https?:\/\/([a-z0-9-]+\.)*korint\.io(:\d+)?(\/|$)/,
        ],
      },
    ],
  });
}

import { defaultNS, Toaster, TooltipProvider, useAssertedParams, useToast } from '@korint/folio';
import { KorintConfigProvider } from 'korint-sdk/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { clientOptions } from './client';
import { Favicon } from './features/Favicon';
import { SetTheme } from './features/setTheme';
import { Title } from './features/Title';
import { UniqAppRouter } from './routes/routes';
import korintConfig from 'virtual:uniq/korint-config';

import './index.css';

import { initI18n } from '@korint/folio';
import tenantFrTranslationsOverride from 'virtual:uniq/translations/locales/fr/translations.json';
import tenantEnTranslationsOverride from 'virtual:uniq/translations/locales/en/translations.json';

initI18n({
  fr: {
    [defaultNS]: tenantFrTranslationsOverride,
  },
  en: {
    [defaultNS]: tenantEnTranslationsOverride,
  },
});

export function App() {
  const toast = useToast();

  return (
    <KorintConfigProvider
      korintConfig={korintConfig}
      clientOptions={clientOptions}
      config={{
        useAutomaticParams: useAssertedParams,
        defaultErrorHandler: error => {
          if ('userError' in error) {
            // Kubb hooks errors
            if (typeof error.userError === 'string') {
              const errorWithTypesFixed = error as unknown as {
                userError: string;
                userHelp: string;
              };
              toast.toast({
                title: errorWithTypesFixed.userError ?? 'Erreur',
                description: errorWithTypesFixed.userHelp,
              });
            }
            // Korint SDK errors
            if (typeof error.userError === 'object') {
              toast.toast({
                title: error.userError.userError ?? 'Erreur',
                description: error.userError.userHelp,
              });
            }
          }
        },
      }}
    >
      <SetTheme />
      <Toaster />
      <TooltipProvider>
        <Title />
        <Favicon />
        <UniqAppRouter />
      </TooltipProvider>
    </KorintConfigProvider>
  );
}

if (!isImpersonationHandoff) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
